import React, { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from './stores/authStore';
import { useGameStore } from './stores/gameStore';
import { useShardStore } from './stores/shardStore';
import { AuthScreen } from './components/AuthScreen';
import { ResourceBar } from './components/ResourceBar';
import { UnitPanel } from './components/UnitPanel';
import { TechPanel } from './components/TechPanel';
import { PvPPanel } from './components/PvPPanel';
import { MapContainer } from './components/MapContainer';
import { NotificationToast } from './components/NotificationToast';
import { connectSocket } from './game/socket';
import type { PlayerSave, ShardState } from '@annopolis/shared';

const SAVE_INTERVAL_MS = 30_000; // auto-save every 30s

export default function App() {
  const { token, playerId, username, clearAuth } = useAuthStore();
  const { isLoaded, loadSave, tick, applyOfflineGains, activeTab, setActiveTab, save } = useGameStore();
  const { setShard } = useShardStore();
  const saveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRafRef = useRef<number | null>(null);

  const isAuthed = !!token && !!playerId;

  // ── Auth → load save ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthed) return;

    async function init() {
      try {
        // Load save (applies offline gains server-side)
        const res = await fetch('/api/game/save', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { clearAuth(); return; }

        const data = await res.json() as PlayerSave & { offlineGains?: { primary: number; knowledge: number; energy: number; pvp: number } };
        const { offlineGains, ...playerSave } = data;

        loadSave({ ...playerSave, playerId: playerId! });

        if (offlineGains && offlineGains.primary > 0) {
          applyOfflineGains(offlineGains);
        }

        // Connect sockets
        connectSocket(token!, playerId!);

        // Join shard
        await fetch('/api/game/shard/join', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });

        // Fetch shard state
        const shardRes = await fetch('/api/game/shard', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (shardRes.ok) {
          const shardData = await shardRes.json() as { shard: ShardState | null };
          setShard(shardData.shard);
        }
      } catch (err) {
        console.error('Init failed:', err);
      }
    }

    init();
  }, [isAuthed]);

  // ── Game tick ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded) return;

    const loop = (time: number) => {
      tick(time);
      tickRafRef.current = requestAnimationFrame(loop);
    };
    tickRafRef.current = requestAnimationFrame(loop);
    return () => { if (tickRafRef.current) cancelAnimationFrame(tickRafRef.current); };
  }, [isLoaded, tick]);

  // ── Auto-save ────────────────────────────────────────────────────────────
  const pushSave = useCallback(async () => {
    if (!token || !isLoaded) return;
    const { save } = useGameStore.getState();
    try {
      await fetch('/api/game/save', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: save.tier,
          ascensions: save.ascensions,
          resources: save.resources,
          lifetimeResources: save.lifetimeResources,
          units: save.units,
          tech: save.tech,
          prestigeMult: save.prestigeMult,
          ascensionMult: save.ascensionMult,
          offlineStart: Date.now(),
        }),
      });
    } catch { /* silent fail — will retry */ }
  }, [token, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    saveTimerRef.current = setInterval(pushSave, SAVE_INTERVAL_MS);
    return () => { if (saveTimerRef.current) clearInterval(saveTimerRef.current); };
  }, [isLoaded, pushSave]);

  // ── Render ───────────────────────────────────────────────────────────────
  if (!isAuthed) return <AuthScreen onAuth={() => {}} />;
  if (!isLoaded) return <LoadingScreen />;

  const tabs: Array<{ key: typeof activeTab; label: string }> = [
    { key: 'units', label: 'Units' },
    { key: 'tech', label: 'Tech' },
    { key: 'pvp', label: 'PvP' },
    { key: 'automation', label: 'Auto' },
  ];

  return (
    <div style={styles.root}>
      <ResourceBar />

      <div style={styles.body}>
        {/* ── Central Map ── */}
        <MapContainer />

        {/* ── Right Panel ── */}
        <div style={styles.rightPanel}>
          <div style={styles.tabBar}>
            {tabs.map((t) => (
              <button
                key={t.key}
                style={{ ...styles.tabBtn, ...(activeTab === t.key ? styles.tabBtnActive : {}) }}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div style={styles.panelContent}>
            {activeTab === 'units' && <UnitPanel />}
            {activeTab === 'tech' && <TechPanel />}
            {activeTab === 'pvp' && <PvPPanel />}
            {activeTab === 'automation' && <AutoPanel />}
          </div>
        </div>
      </div>

      {/* Bottom strip */}
      <div style={styles.bottomStrip}>
        <span style={styles.playerBadge}>⬡ {username}</span>
        <span style={styles.saveHint}>Auto-saves every 30s</span>
        <button style={styles.logoutBtn} onClick={() => { clearAuth(); }}>Logout</button>
      </div>

      <NotificationToast />
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#4af0c4', letterSpacing: '0.15em' }}>ANNOPOLIS</div>
      <div style={{ fontSize: 12, color: '#4a6080' }}>Initializing systems...</div>
    </div>
  );
}

function AutoPanel() {
  const { autobuyEnabled, setAutobuy, save } = useGameStore();
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4af0c4', fontWeight: 700 }}>Automation</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={autobuyEnabled}
          onChange={(e) => setAutobuy(e.target.checked)}
          style={{ accentColor: '#4af0c4', width: 14, height: 14 }}
        />
        <span style={{ fontSize: 13, color: '#c8d8f0' }}>Enable Auto-buy</span>
      </label>
      <p style={{ fontSize: 11, color: '#4a6080', lineHeight: 1.5 }}>
        Automatically purchases the most cost-effective units when resources allow.
        Unlock Smart Allocation in the Tech tree to optimize by ROI.
      </p>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100vw',
    height: '100vh',
    background: '#0a0e1a',
    overflow: 'hidden',
  },
  body: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  rightPanel: {
    width: 320,
    display: 'flex',
    flexDirection: 'column',
    borderLeft: '1px solid rgba(74,240,196,0.12)',
    background: 'rgba(10,14,26,0.98)',
    flexShrink: 0,
  },
  tabBar: {
    display: 'flex',
    borderBottom: '1px solid rgba(74,240,196,0.1)',
    background: 'rgba(10,14,26,0.95)',
  },
  tabBtn: {
    flex: 1,
    padding: '9px 4px',
    background: 'none',
    border: 'none',
    color: '#4a6080',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    cursor: 'pointer',
    transition: 'all 0.15s',
    borderBottom: '2px solid transparent',
  },
  tabBtnActive: {
    color: '#4af0c4',
    borderBottom: '2px solid #4af0c4',
    background: 'rgba(74,240,196,0.05)',
  },
  panelContent: { flex: 1, overflow: 'hidden' },
  bottomStrip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 16px',
    borderTop: '1px solid rgba(74,240,196,0.1)',
    background: 'rgba(10,14,26,0.95)',
    height: 28,
  },
  playerBadge: { fontSize: 11, color: '#4af0c4', fontWeight: 600 },
  saveHint: { fontSize: 10, color: '#2a3a5a' },
  logoutBtn: {
    fontSize: 10, color: '#4a6080', background: 'none', border: 'none',
    cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em',
  },
};
